import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { platformAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import AccordionRow from '../../components/ui/AccordionRow';
import {
  AdminDrawerShell,
  AdminEmptyState,
  AdminFilterCard,
  AdminModalShell,
  AdminPageHeader,
  AdminStatCard,
  AdminStatGrid,
  AdminTableCard,
} from '../../components/AdminSurface';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

function PlatformInvoices() {
  const location = useLocation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);

  const { data: stats, isLoading: statsLoading } = useQuery(
    'platform-invoice-stats',
    () => platformAPI.getInvoiceStats(),
    { select: (response) => response.data }
  );

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery(
    ['platform-invoices', statusFilter, companyFilter],
    () => platformAPI.getInvoices({
      status: statusFilter || undefined,
      companyId: companyFilter || undefined,
    }),
    { select: (response) => response.data?.invoices || response.data || [] }
  );

  useEffect(() => {
    const state = location.state as {
      openInvoiceId?: string;
      openCustomerId?: string;
      openCustomerEmail?: string;
      openCompanyId?: string;
    } | null;

    if (!state?.openCompanyId) {
      return;
    }

    setCompanyFilter(state.openCompanyId);
    setStatusFilter('');
  }, [location.state]);

  useEffect(() => {
    const state = location.state as {
      openInvoiceId?: string;
      openCustomerId?: string;
      openCustomerEmail?: string;
      openCompanyId?: string;
    } | null;

    if (!state || !Array.isArray(invoices) || invoices.length === 0) {
      return;
    }

    if (state.openInvoiceId) {
      const invoice = invoices.find((item: any) => item.id === state.openInvoiceId);
      if (invoice) {
        setSelectedInvoice(invoice);
      }
    }

    if (state.openCustomerId || state.openCustomerEmail) {
      const customerMatch = invoices.find((item: any) =>
        (state.openCustomerId && item.customer_id === state.openCustomerId) ||
        (state.openCustomerEmail && item.customer_email === state.openCustomerEmail)
      );

      if (customerMatch) {
        setSelectedCustomer({
          id: customerMatch.customer_id || state.openCustomerId || null,
          email: customerMatch.customer_email || state.openCustomerEmail || 'Unknown customer',
          company_id: customerMatch.company_id,
          company_name: customerMatch.company_name,
        });
      }
    }

    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [invoices, location.pathname, location.search, location.state, navigate]);

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      paid: 'border border-outline/70 bg-success-container text-on-success-container',
      open: 'border border-outline/70 bg-info-container text-on-info-container',
      draft: 'border border-outline/70 bg-surface-container-low text-on-surface',
      void: 'border border-outline/70 bg-error-container text-on-error-container',
      uncollectible: 'border border-outline/70 bg-error-container text-on-error-container',
    };
    return (
      <span className={`admin-soft-pill inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${colors[status] || 'border border-outline/70 bg-surface-container-low text-on-surface'}`}>
        {status}
      </span>
    );
  };

  const selectedCustomerInvoices = useMemo(() => {
    if (!selectedCustomer) {
      return [];
    }

    return invoices.filter((invoice: any) => {
      const sameCompany = selectedCustomer.company_id
        ? invoice.company_id === selectedCustomer.company_id
        : true;
      const sameCustomerId = selectedCustomer.id ? invoice.customer_id === selectedCustomer.id : false;
      const sameCustomerEmail = selectedCustomer.email ? invoice.customer_email === selectedCustomer.email : false;
      return sameCompany && (sameCustomerId || sameCustomerEmail);
    });
  }, [invoices, selectedCustomer]);

  type PlatformInvoiceRow =
    | {
        type: 'group';
        requestId: string;
        invoices: any[];
      }
    | {
        type: 'single';
        invoice: any;
      };

  const ledgerRows = useMemo<PlatformInvoiceRow[]>(() => {
    const groupedByRequest = new Map<string, any[]>();

    for (const invoice of invoices) {
      if (invoice.grouped_payment_request_id && invoice.grouped_payment_request_invoice_count > 1) {
        const group = groupedByRequest.get(invoice.grouped_payment_request_id) || [];
        group.push(invoice);
        groupedByRequest.set(invoice.grouped_payment_request_id, group);
      }
    }

    const seenRequestIds = new Set<string>();
    const rows: PlatformInvoiceRow[] = [];

    for (const invoice of invoices) {
      if (invoice.grouped_payment_request_id && invoice.grouped_payment_request_invoice_count > 1) {
        const requestId = invoice.grouped_payment_request_id;
        if (seenRequestIds.has(requestId)) {
          continue;
        }

        seenRequestIds.add(requestId);
        rows.push({
          type: 'group',
          requestId,
          invoices: groupedByRequest.get(requestId) || [invoice],
        });
        continue;
      }

      rows.push({ type: 'single', invoice });
    }

    return rows;
  }, [invoices]);

  const openInvoiceModal = (invoice: any) => {
    setSelectedInvoice(invoice);
  };

  const openCustomerDrawer = (invoice: any) => {
    setSelectedCustomer({
      id: invoice.customer_id || null,
      email: invoice.customer_email || 'Unknown customer',
      company_id: invoice.company_id,
      company_name: invoice.company_name || 'Unknown company',
    });
  };

  // Unique companies from invoices for filter
  const companies = Array.from(
    new Map(
      invoices
        .filter((inv: any) => inv.company_name)
        .map((inv: any) => [inv.company_id, { id: inv.company_id, name: inv.company_name }])
    ).values()
  );

  return (
    <PageLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title="Platform Invoices"
          description="Track cross-tenant invoice volume, platform-fee collection, and customer payment details from one shared surface."
          eyebrow="Finance operations"
        />

        {statsLoading ? (
          <div className="flex justify-center py-6"><LoadingSpinner /></div>
        ) : stats ? (
          <AdminStatGrid>
            <AdminStatCard
              icon={<DocumentTextIcon className="h-8 w-8" />}
              label="Total Invoices"
              value={stats.total_invoices}
              tone="info"
            />
            <AdminStatCard
              icon={<CurrencyDollarIcon className="h-8 w-8" />}
              label="Total Platform Revenue"
              value={formatCurrency(stats.total_platform_revenue)}
              tone="success"
            />
            <AdminStatCard
              icon={<CheckCircleIcon className="h-8 w-8" />}
              label="Paid Fees"
              value={formatCurrency(stats.paid_fees)}
              tone="success"
            />
            <AdminStatCard
              icon={<ClockIcon className="h-8 w-8" />}
              label="Outstanding Fees"
              value={formatCurrency(stats.outstanding_fees)}
              tone="warning"
            />
          </AdminStatGrid>
        ) : null}

        <AdminFilterCard
          title="Filters"
          description="Focus the ledger by invoice lifecycle state and tenant company."
        >
          <div className="flex flex-col gap-4 md:flex-row">
            <select
              className="input-field w-full md:w-56"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="open">Open</option>
              <option value="draft">Draft</option>
              <option value="void">Void</option>
            </select>
            <select
              className="input-field w-full md:w-64"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="">All Companies</option>
              {companies.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </AdminFilterCard>

        <AdminTableCard
          title="Platform fee ledger"
          description="Review invoice totals, company ownership, and platform fee collection across all tenants."
          meta={invoicesLoading ? 'Loading invoices...' : `${invoices.length} invoices in scope`}
        >
          <div className="overflow-x-auto">
            <table className="admin-table-grid min-w-full">
              <thead className="admin-table-head">
              <tr>
                <th className="w-14 px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant" />
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Company</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Customer</th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Total</th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Platform Fee</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Date</th>
              </tr>
              </thead>
              {invoicesLoading ? (
                <tbody className="admin-table-body">
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center">
                      <LoadingSpinner />
                    </td>
                  </tr>
                </tbody>
              ) : invoices.length === 0 ? (
                <tbody className="admin-table-body">
                  <tr>
                    <td colSpan={8} className="p-0">
                      <AdminEmptyState
                        icon={<DocumentTextIcon className="h-6 w-6" />}
                        title="No invoices found"
                        description="Adjust the company or status filters to broaden the ledger view."
                      />
                    </td>
                  </tr>
                </tbody>
              ) : (
                ledgerRows.map((row) => {
                  if (row.type === 'group') {
                    const representative = row.invoices[0];
                    const total = row.invoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);
                    const platformFee = row.invoices.reduce((sum, invoice) => sum + (invoice.platform_fee || 0), 0);
                    const issuedAt = representative.issued_at ? new Date(representative.issued_at).toLocaleDateString() : '—';
                    const statuses = Array.from(new Set(row.invoices.map((invoice) => invoice.status)));
                    const statusLabel = statuses.length === 1 ? statuses[0] : 'grouped';

                    return (
                      <AccordionRow
                        key={`platform-request-${row.requestId}`}
                        id={`platform-request-${row.requestId}`}
                        expandable={true}
                        summaryCells={[
                          <div>
                            <div className="font-medium text-on-surface">Grouped payment {row.requestId.slice(0, 8)}</div>
                            <div className="text-xs text-on-surface-variant">{row.invoices.length} invoices</div>
                          </div>,
                          <div className="text-sm text-on-surface">{representative.company_name || '—'}</div>,
                          <button
                            type="button"
                            className="text-left text-sm transition-colors hover:text-primary hover:underline"
                            onClick={() => openCustomerDrawer(representative)}
                          >
                            {representative.customer_email || '—'}
                          </button>,
                          <div className="text-right text-sm text-on-surface">{formatCurrency(total)}</div>,
                          <div className="text-right text-sm font-medium text-success">{formatCurrency(platformFee)}</div>,
                          <div className="text-sm">{statusBadge(statusLabel)}</div>,
                          <div className="text-sm text-on-surface-variant">{issuedAt}</div>,
                        ]}
                        detailContent={
                          <div className="space-y-3">
                            <div className="text-sm font-semibold text-on-surface">Included invoices</div>
                            <div className="overflow-x-auto rounded-card border border-outline/60 bg-surface-container">
                              <table className="admin-table-grid min-w-full">
                                <thead className="admin-table-head">
                                  <tr>
                                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Invoice #</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Company</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Customer</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Total</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Platform Fee</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Date</th>
                                  </tr>
                                </thead>
                                <tbody className="admin-table-body">
                                  {row.invoices.map((invoice) => (
                                    <tr key={invoice.id} className="admin-table-row">
                                      <td className="px-5 py-4 text-sm text-on-surface">
                                        <button
                                          type="button"
                                          className="text-left font-semibold text-on-surface transition-colors hover:text-primary hover:underline"
                                          onClick={() => openInvoiceModal(invoice)}
                                        >
                                          {invoice.invoice_number || invoice.id.slice(0, 8)}
                                        </button>
                                      </td>
                                      <td className="px-6 py-4 text-sm text-on-surface">{invoice.company_name || '—'}</td>
                                      <td className="px-6 py-4 text-sm text-on-surface-variant">
                                        <button
                                          type="button"
                                          className="text-left transition-colors hover:text-primary hover:underline"
                                          onClick={() => openCustomerDrawer(invoice)}
                                        >
                                          {invoice.customer_email || '—'}
                                        </button>
                                      </td>
                                      <td className="px-6 py-4 text-right text-sm text-on-surface">{formatCurrency(invoice.total)}</td>
                                      <td className="px-6 py-4 text-right text-sm font-medium text-success">{formatCurrency(invoice.platform_fee || 0)}</td>
                                      <td className="px-6 py-4 text-sm">{statusBadge(invoice.status)}</td>
                                      <td className="px-6 py-4 text-sm text-on-surface-variant">{invoice.issued_at ? new Date(invoice.issued_at).toLocaleDateString() : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        }
                      />
                    );
                  }

                  const inv = row.invoice;

                  return (
                    <AccordionRow
                      key={inv.id}
                      id={`platform-invoice-${inv.id}`}
                      expandable={false}
                      summaryCells={[
                        <button
                          type="button"
                          className="text-left font-medium text-on-surface transition-colors hover:text-primary hover:underline"
                          onClick={() => openInvoiceModal(inv)}
                        >
                          {inv.invoice_number || inv.id.slice(0, 8)}
                        </button>,
                        <div className="text-sm text-on-surface">{inv.company_name || '—'}</div>,
                        <button
                          type="button"
                          className="text-left text-sm transition-colors hover:text-primary hover:underline"
                          onClick={() => openCustomerDrawer(inv)}
                        >
                          {inv.customer_email || '—'}
                        </button>,
                        <div className="text-right text-sm text-on-surface">{formatCurrency(inv.total)}</div>,
                        <div className="text-right text-sm font-medium text-success">{formatCurrency(inv.platform_fee)}</div>,
                        <div className="text-sm">{statusBadge(inv.status)}</div>,
                        <div className="text-sm text-on-surface-variant">{inv.issued_at ? new Date(inv.issued_at).toLocaleDateString() : '—'}</div>,
                      ]}
                    />
                  );
                })
              )}
            </table>
          </div>
        </AdminTableCard>

        {selectedInvoice && (
          <AdminModalShell
            title="Invoice Details"
            description="Cross-tenant invoice metadata, payment state, and platform fee summary."
            onClose={() => setSelectedInvoice(null)}
            maxWidthClassName="max-w-2xl"
          >
            <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
              <div>
                <p className="text-on-surface-variant">Invoice</p>
                <p className="font-semibold text-on-surface">{selectedInvoice.invoice_number || selectedInvoice.id}</p>
              </div>
              <div>
                <p className="text-on-surface-variant">Status</p>
                <div>{statusBadge(selectedInvoice.status)}</div>
              </div>
              <div>
                <p className="text-on-surface-variant">Company</p>
                <p className="font-medium text-on-surface">{selectedInvoice.company_name || '—'}</p>
              </div>
              <div>
                <p className="text-on-surface-variant">Customer</p>
                <p className="font-medium text-on-surface">{selectedInvoice.customer_email || '—'}</p>
              </div>
              <div>
                <p className="text-on-surface-variant">Invoice Total</p>
                <p className="font-semibold text-on-surface">{formatCurrency(selectedInvoice.total)}</p>
              </div>
              <div>
                <p className="text-on-surface-variant">Platform Fee</p>
                <p className="font-semibold text-success">{formatCurrency(selectedInvoice.platform_fee)}</p>
              </div>
              <div>
                <p className="text-on-surface-variant">Paid At</p>
                <p className="text-on-surface">{selectedInvoice.paid_at ? new Date(selectedInvoice.paid_at).toLocaleString() : '—'}</p>
              </div>
              <div>
                <p className="text-on-surface-variant">Stripe Invoice</p>
                <p className="font-mono text-xs text-on-surface">{selectedInvoice.stripe_invoice_id || '—'}</p>
              </div>
            </div>
          </AdminModalShell>
        )}

        {selectedCustomer && (
          <AdminDrawerShell
            title="Customer Details"
            description={selectedCustomer.email}
            onClose={() => setSelectedCustomer(null)}
          >
            <div className="space-y-6">
              <div className="admin-subpanel rounded-card p-4">
              <p className="text-xs uppercase tracking-wide text-on-surface-variant">Company</p>
              <p className="mt-1 font-medium text-on-surface">{selectedCustomer.company_name || 'Unknown company'}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-on-surface-variant">Recent Invoices</h3>
                {selectedCustomerInvoices.length === 0 ? (
                  <p className="mt-2 text-sm text-on-surface-variant">No invoices available for this customer in the current scope.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {selectedCustomerInvoices.slice(0, 12).map((invoice: any) => (
                      <button
                        key={invoice.id}
                        type="button"
                        onClick={() => openInvoiceModal(invoice)}
                        className="admin-subpanel w-full rounded-card px-4 py-3 text-left transition-all hover:-translate-y-0.5"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-on-surface">{invoice.invoice_number || invoice.id.slice(0, 8)}</span>
                          <span className="text-sm text-on-surface">{formatCurrency(invoice.total)}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs text-on-surface-variant">
                          <span>{invoice.issued_at ? new Date(invoice.issued_at).toLocaleDateString() : '—'}</span>
                          <span>{invoice.status}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </AdminDrawerShell>
        )}
      </div>
    </PageLayout>
  );
}

export default PlatformInvoices;
